import React, { createContext } from 'react';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { addAdvancedSettingValues } from '@hooks/application/useAdvancedSettings';
import { logError, logFormSubmit, logNetRequest, scrollToTop } from '@utils/usefulFuncs';
import { AdvancedSettingsContext } from './AdvancedSettingsContext';
import { ModeContext } from './ModeContext';
import { parseReasoningResult } from '@features/querySeries/utils/parseReasoningResult';
import { useQuerySeriesResults } from '@features/querySeries/hooks/useQuerySeriesResults';

const ROW_STATUS = {
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
};

const ACTIONS = {
  setQuestions: 'setQuestions',
  startSubmit: 'startSubmit',
  startSeries: 'startSeries',
  rowMessage: 'rowMessage',
  seriesComplete: 'seriesComplete',
  seriesError: 'seriesError',
  updateRow: 'updateRow',
  reset: 'reset',
};

const emptyQuestion = { question: '', displayQuestion: '' };

const initialState = {
  questions: [emptyQuestion],
  rows: [],
  groupResultId: null,
  isSubmittingToApi: false,
  isStreaming: false,
  formError: null,
};

const defaultState = {
  ...initialState,
  setQuestions: () => logError('setQuestions not initialized'),
  submitSeries: () => logError('submitSeries not initialized'),
  resetSeries: () => logError('resetSeries not initialized'),
  updateRow: () => logError('updateRow not initialized'),
  getRowResult: () => null,
  isRowComplete: () => false,
  isSeriesComplete: false,
  streamError: null,
  defaultDocs: [],
};

const toQuestionRows = (questions) =>
  questions.map(({ question, displayQuestion }, rowIndex) => ({
    rowIndex,
    question,
    displayQuestion: displayQuestion || question,
    rawMessage: '',
    answer: null,
    reasoning: null,
    status: ROW_STATUS.RUNNING,
    error: null,
  }));

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.setQuestions: {
      return { ...state, questions: action.payload };
    }
    case ACTIONS.startSubmit: {
      return { ...state, isSubmittingToApi: true, formError: null };
    }
    case ACTIONS.startSeries: {
      const { questions, groupResultId } = action.payload;
      return {
        ...state,
        isSubmittingToApi: false,
        isStreaming: true,
        formError: null,
        groupResultId,
        rows: toQuestionRows(questions),
      };
    }
    case ACTIONS.rowMessage: {
      const { rowIndex, message } = action.payload;
      const parsed = parseReasoningResult(message);
      return {
        ...state,
        rows: state.rows.map((row) => {
          if (row.rowIndex !== rowIndex) return row;
          return {
            ...row,
            rawMessage: message,
            answer: parsed?.answer || row.answer,
            reasoning: parsed?.reasoning || row.reasoning,
          };
        }),
      };
    }
    case ACTIONS.seriesComplete: {
      const { answersWithContexts } = action.payload;
      return {
        ...state,
        isStreaming: false,
        rows: state.rows.map((row, rowIndex) => {
          const completedResult = answersWithContexts[rowIndex];
          const finalRawMessage = completedResult?.answer || row.rawMessage || '';
          const parsed = parseReasoningResult(finalRawMessage);
          return {
            ...row,
            rawMessage: finalRawMessage,
            answer: parsed?.answer || row.answer,
            reasoning: parsed?.reasoning || row.reasoning,
            status: ROW_STATUS.COMPLETED,
            error: null,
          };
        }),
      };
    }
    case ACTIONS.seriesError: {
      return {
        ...state,
        isSubmittingToApi: false,
        isStreaming: false,
        formError: action.payload,
        rows: state.rows.map((row) => {
          if (row.status === ROW_STATUS.COMPLETED) return row;
          return { ...row, status: ROW_STATUS.ERROR, error: action.payload };
        }),
      };
    }
    case ACTIONS.updateRow: {
      const { rowIndex, answer, reasoning } = action.payload;
      return {
        ...state,
        rows: state.rows.map((row) => {
          if (row.rowIndex !== rowIndex) return row;
          return { ...row, answer, reasoning };
        }),
      };
    }
    case ACTIONS.reset: {
      const sq = action.payload?.startingQuestions;
      return {
        ...initialState,
        questions: sq?.length > 0 ? sq : initialState.questions,
      };
    }
    default: {
      return state;
    }
  }
}

export const QuerySeriesContext = createContext(defaultState);

export const QuerySeriesProvider = ({ children }) => {
  const advancedSettingsValues = React.useContext(AdvancedSettingsContext);
  const { promptSuffix, tool, startingQuestions } = React.useContext(ModeContext);
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    questions: startingQuestions?.length > 0 ? startingQuestions : initialState.questions,
  });
  const [defaultDocs, setDefaultDocs] = React.useState([]);
  const { startStream, cancelStream, resetResults, getRowResult, isRowComplete, isSeriesComplete, streamError } =
    useQuerySeriesResults();

  React.useEffect(() => {
    const canceled = { isCanceled: false };
    const fetchDefaultDocs = async () => {
      const URL = `v1/genai/documents/default?user_mode=${tool}`;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(URL, status, data);
      handle400Statuses(status);
      if (status === 200 && !canceled.isCanceled && data?.documents?.length > 0) {
        setDefaultDocs(data.documents);
      }
    };
    fetchDefaultDocs();
    return () => {
      canceled.isCanceled = true;
    };
  }, [tool]);

  const setQuestions = React.useCallback((questions) => {
    dispatch({ type: ACTIONS.setQuestions, payload: questions });
  }, []);

  const submitSeries = React.useCallback(
    async ({ questions }) => {
      const cleanQuestions = (questions || [])
        .map((item) => ({
          question: item.question.trim(),
          displayQuestion: item.displayQuestion.trim() || item.question.trim(),
        }))
        .filter((item) => item.question);
      if (cleanQuestions.length === 0) {
        dispatch({ type: ACTIONS.seriesError, payload: 'Please enter at least one question.' });
        return false;
      }

      dispatch({ type: ACTIONS.startSubmit });

      const docList = defaultDocs.map((doc) => (typeof doc === 'object' ? doc.id : doc));
      const questionsWithSuffix = cleanQuestions.map(({ question }) =>
        promptSuffix ? `${question} ${promptSuffix}` : question
      );

      scrollToTop();

      const settings = { user_mode: tool, include_context: true };
      addAdvancedSettingValues(advancedSettingsValues, settings);
      const v2Questions = questionsWithSuffix.map((q) => ({ question: q }));
      logFormSubmit({ ...settings, questions: v2Questions, doc_ids: docList });
      const result = await submitQueriesStream({
        questions: v2Questions,
        docIds: docList.length > 0 ? docList : undefined,
        settings,
      });
      if (result.status !== 200) {
        dispatch({
          type: ACTIONS.seriesError,
          payload: result.error || 'Something went wrong, try again.',
        });
        return false;
      }
      const groupResultId = result.runId;

      if (!groupResultId) {
        dispatch({ type: ACTIONS.seriesError, payload: 'Result id was missing from response.' });
        return false;
      }

      dispatch({ type: ACTIONS.startSeries, payload: { questions: cleanQuestions, groupResultId } });
      startStream({
        groupResultId,
        onRowMessage: ({ rowIndex, message }) => dispatch({ type: ACTIONS.rowMessage, payload: { rowIndex, message } }),
        onSeriesComplete: ({ answersWithContexts }) =>
          dispatch({ type: ACTIONS.seriesComplete, payload: { answersWithContexts } }),
        onSeriesError: (message) => dispatch({ type: ACTIONS.seriesError, payload: message || 'Streaming failed' }),
      });

      return true;
    },
    [advancedSettingsValues, defaultDocs, promptSuffix, startStream, tool]
  );

  const updateRow = React.useCallback((rowIndex, answer, reasoning) => {
    dispatch({ type: ACTIONS.updateRow, payload: { rowIndex, answer, reasoning } });
  }, []);

  const resetSeries = React.useCallback(() => {
    cancelStream();
    resetResults();
    dispatch({ type: ACTIONS.reset, payload: { startingQuestions } });
  }, [cancelStream, resetResults, startingQuestions]);

  React.useEffect(() => {
    if (streamError) {
      dispatch({ type: ACTIONS.seriesError, payload: streamError });
    }
  }, [streamError]);

  React.useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  const value = React.useMemo(
    () => ({
      ...state,
      setQuestions,
      submitSeries,
      resetSeries,
      updateRow,
      getRowResult,
      isRowComplete,
      isSeriesComplete,
      streamError,
      defaultDocs,
    }),
    [
      state,
      setQuestions,
      submitSeries,
      resetSeries,
      updateRow,
      getRowResult,
      isRowComplete,
      isSeriesComplete,
      streamError,
      defaultDocs,
    ]
  );

  return <QuerySeriesContext.Provider value={value}>{children}</QuerySeriesContext.Provider>;
};
